import React from 'react';
import PropTypes from 'prop-types';

import { Utr, UtrLabel } from '../../../text';
import { utrTypeIds } from '../../../../utils/eventConstants';
import { MatchFormatTypes } from '../../../../utils/postScoreConstants';
import { RatingDisplayStates } from '../../../../utils/constants';
import { getRatingDetails, setRatingChoice } from '../../../../utils';

// Hover text per display state (TMS-2226). Title Case per the Figma tooltip frame (node 1245-21087) -
// the ticket's AC table used sentence case, but design wins where the two disagree. A state absent
// from this map falls back to the raw status, which is what every other surface shows.
const RATING_TOOLTIPS = {
    [RatingDisplayStates.FULLY_RELIABLE]: '100% Reliable',
    [RatingDisplayStates.PROJECTED]: 'Projected',
    [RatingDisplayStates.ESTIMATED]: 'Estimated Range',
    [RatingDisplayStates.HISTORIC]: 'Historic Rating',
    [RatingDisplayStates.UNRATED]: 'Unrated',
};

const Rating = props => {
    const { player, ratingType, utrType, ratingStatus, showDecimals, showProjectedViewStatus, hideUtrLabel, sportTypeId, isPickleballRating, isItGlobalSearch, showTypeDots, showReliability, superscriptHistoricMarker, hideHistoricMarker, historicVerifiedIcon, verifiedTooltip, statusTooltip, emphasizeValue, 'data-testid': dataTestId } = props;
    const doublesPicklebalLabel = player.doublesPkbRatingDisplay?.reliability;
    const singlePicklebalLabel = player.singlesPkbRatingDisplay?.reliability;
    const hideReliableForSinlgePkb = ratingType === MatchFormatTypes.SINGLES && singlePicklebalLabel === 'Reliable';
    const hideReliableForDoublesPkb = ratingType === MatchFormatTypes.DOUBLES && doublesPicklebalLabel === 'Reliable';
    const hideReliableLabelForPkb = hideReliableForSinlgePkb || hideReliableForDoublesPkb;
    const isSanctioned = utrType === 'sanctioned' || utrType === utrTypeIds.SANCTIONED;
    // Only resolved when a caller opts in, so the 14 render sites that do not are unaffected by the
    // extra work.
    const displayState = statusTooltip
        ? getRatingDetails(setRatingChoice({ player, ratingType, utrType })).displayState
        : null;
    const tooltip = RATING_TOOLTIPS[displayState] ?? ratingStatus;
    // The design calls for a styled hover chip rather than the OS-native tooltip, so once the chip
    // actually renders (statusTooltip on a sanctioned utrType) the native `title` is dropped entirely
    // - keeping both would double up the hover text.
    const showTooltipChip = statusTooltip && isSanctioned;
    // Hoisted rather than inlined into `title` below: nesting it there reads as a nested ternary.
    // `''` and not `undefined` for the non-sanctioned case, which is the pre-existing behaviour -
    // a myUtr rating has never carried the status as its hover text.
    const nativeTitle = isSanctioned ? tooltip : '';
    // `.text-lighter` sets its colour with `!important`, so a caller wanting the value at full
    // emphasis has to stop the class being emitted - no stylesheet can outrank it.
    const muteValue = !emphasizeValue && ratingStatus !== 'Rated' && !hideReliableLabelForPkb;
    return (
        <div className="values" data-testid={dataTestId && `${dataTestId}.values`}>
            <div
                className={`value ${muteValue ? 'text-lighter' : ''}`}
                // `undefined` (not `''`) once the chip renders: React renders `title=""` for an
                // empty string, which would still pair up with the chip as a second, blank tooltip.
                // Keyed off `showTooltipChip`, not `statusTooltip` alone, so a non-sanctioned opt-in
                // caller still gets its native title instead of losing the hover text entirely.
                title={showTooltipChip ? undefined : nativeTitle}
                // The chip is revealed by `:hover` alone from a `visibility: hidden` base, which keeps
                // it out of the accessibility tree and unreachable without a pointer. Dropping the
                // native `title` would therefore have taken the status away from screen-reader and
                // keyboard users entirely, so the status is carried here instead - an accessible name
                // renders nothing visually, so it cannot double up with the chip.
                // `role="group"` is load-bearing, not decoration: a bare `div` has the implicit role
                // `generic`, for which ARIA *prohibits* an author-supplied name, so `aria-label` alone
                // would sit in the DOM and be ignored by assistive tech. `group` permits naming.
                // Matches `PlayersDrawsPanel`'s `role="group" aria-label={...}` in this same feature.
                role={showTooltipChip ? 'group' : undefined}
                aria-label={showTooltipChip ? tooltip : undefined}
                data-testid={dataTestId && `${dataTestId}.utr`}
            >
                <Utr player={player} force={{ ratingType, utrType, verifiedIcon: true, showDecimals, showProjectedViewStatus, superscriptHistoricMarker, hideHistoricMarker, historicVerifiedIcon, verifiedTooltip }} sportTypeId={sportTypeId} isPickleballRating={isPickleballRating} isItGlobalSearch={isItGlobalSearch} data-testid={dataTestId && `${dataTestId}.utr`} />
                {showTooltipChip && (
                    // Purely decorative: `aria-label` above is what exposes the status, so the chip
                    // stays hidden from assistive tech rather than repeating it. `role="tooltip"` is
                    // dropped with it - the role is only meaningful when a trigger points at the node
                    // via `aria-describedby`, and nothing does.
                    <span className="ratingTooltip" aria-hidden="true" data-testid={dataTestId && `${dataTestId}.tooltip`}>
                        {tooltip}
                    </span>
                )}
            </div>
            <div className="title" data-testid={dataTestId && `${dataTestId}.label`}>
                <UtrLabel player={player} force={{ ratingType, utrType, typeDots: showTypeDots, hideUtrLabel, showReliability }} sportTypeId={sportTypeId} isPickleballRating={isPickleballRating} hideReliableLabelForPkb={hideReliableLabelForPkb} isItGlobalSearch={isItGlobalSearch} data-testid={dataTestId && `${dataTestId}.label`} />
            </div>
        </div>
    );
};

Rating.propTypes = {
    isPickleballRating: PropTypes.bool,
    isItGlobalSearch: PropTypes.bool,
    showTypeDots: PropTypes.bool,
    showReliability: PropTypes.bool,
    superscriptHistoricMarker: PropTypes.bool,
    hideHistoricMarker: PropTypes.bool,
    historicVerifiedIcon: PropTypes.bool,
    verifiedTooltip: PropTypes.bool,
    statusTooltip: PropTypes.bool,
    emphasizeValue: PropTypes.bool,
    'data-testid': PropTypes.string,
};
Rating.defaultProps = {
    isPickleballRating: false,
    isItGlobalSearch: false,
    showTypeDots: true,
    showReliability: false,
    superscriptHistoricMarker: false,
    hideHistoricMarker: false,
    historicVerifiedIcon: false,
    verifiedTooltip: false,
    statusTooltip: false,
    emphasizeValue: false,
    'data-testid': null,
};
export default Rating;
